<script lang="ts" module>
	export interface ModalProps {
		onclose?: () => void;
		onopen?: () => void;
		onhide?: (payload?: Record<string, unknown>) => void;
		children: Snippet;
		size?: 'small' | 'regular' | 'fullscreen';
	}
</script>

<script lang="ts">
	import Button from '$lib/buttons/v3/button.svelte';
	import Close from '$lib/icons/close.svelte';
	import type { Snippet } from 'svelte';

	let { onclose, onopen, onhide, children, size = 'regular' }: ModalProps = $props();

	let modal = $state<HTMLDialogElement>();
	let modalState = $state<null | 'opening' | 'closing'>(null);

	export function show() {
		if (!modal) return;

		if (!document.body.contains(modal)) {
			document.body.appendChild(modal);
		}
		modal.classList.remove('hidden');
		modal.classList.add('flex');

		modal.showModal();
		modal.focus(); // we don't want to initially focus a button
		modalState = 'opening';
		onopen?.();
	}

	let closeTimeout: NodeJS.Timeout;
	export function hide(payload?: Record<string, unknown>) {
		modalState = 'closing';

		closeTimeout = setTimeout(() => {
			if (modal) {
				modal.close();
				modal.remove();
				modal.classList.remove('flex');
				modal.classList.add('hidden');
			}
			modalState = null;
		}, 500);

		onhide?.(payload);
	}

	export function remove() {
		clearTimeout(closeTimeout);

		if (!modal || !document.body.contains(modal)) return;

		modal.remove();
	}

	$effect(() => {
		if (modal) {
			document.body.appendChild(modal);
		}

		return () => {
			remove();
		};
	});

	let desktopSize = $derived.by(() => {
		switch (size) {
			case 'small':
				return '--desktop-height: fit-content; --desktop-width: 491px; --desktop-max-height: 806px;';
			case 'regular':
				return '--desktop-height: 90svh; --desktop-width: 830px; --desktop-max-height: 806px;';
			case 'fullscreen':
				return '--desktop-height: 100svh; --desktop-width: 100vw; --desktop-max-height: 100svh;';
			default:
				return '--desktop-height: 90svh; --desktop-width: 830px; --desktop-max-height: 806px;';
		}
	});
</script>

<!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
<!-- svelte-ignore a11y_click_events_have_key_events -->
<dialog
	data-testid="modal-dialog"
	bind:this={modal}
	onclose={() => {
		modalState = 'closing';
		onclose?.();
	}}
	class={[
		'fixed z-50 m-auto hidden w-full justify-center overflow-visible bg-transparent antialiased outline-none',
		{
			'lg:p-5': size === 'fullscreen'
		}
	]}
	class:dialog-in={modalState === 'opening'}
	class:dialog-out={modalState === 'closing'}
	onclick={(e) => {
		// don't trigger any action behind the modal
		e.stopImmediatePropagation();

		if (e.target === modal) {
			e.preventDefault();
			hide?.();
		}
	}}
	style={desktopSize}
>
	<div class="modal-content relative h-full w-full overflow-hidden bg-white text-brown-1">
		{@render children()}
		<div class="absolute right-4 top-4" data-testid="modal-close-wrapper">
			<Button class="btn-white" onclick={() => hide()} title="Close modal"><Close /></Button>
		</div>
	</div>
</dialog>

<style lang="scss">
	@keyframes dialogIn {
		from {
			transform: translate3d(0, 100vh, 0);
		}
		to {
			transform: translate3d(0, 0, 0);
		}
	}

	@keyframes dialogOut {
		from {
			transform: translate3d(0, 0, 0);
		}
		to {
			transform: translate3d(0, 100vh, 0);
		}
	}

	@keyframes fadeIn {
		from {
			opacity: 0.01;
		}
		to {
			opacity: 1;
		}
	}

	@keyframes fadeOut {
		from {
			opacity: 1;
		}
		to {
			opacity: 0;
		}
	}

	dialog {
		height: 100dvh;
		margin: 0;
		max-width: 100svw;
		max-height: 100dvh;

		@media (max-height: 806px) and (min-width: 831px) and (orientation: landscape) and (pointer: coarse) {
			max-width: 580px;
		}

		@media (min-width: 831px) and (min-height: 807px) {
			max-width: var(--desktop-width);
		}

		@media (min-width: 831px) {
			height: var(--desktop-height);
			margin: auto;
			max-height: var(--desktop-max-height);
			& div.modal-content {
				border-radius: 20px;
			}
		}

		& div.modal-content {
			animation-fill-mode: forwards;

			@media (min-width: 831px) {
				box-shadow: 0px 16px 32px rgba(0, 0, 0, 0.16);
			}
		}

		&.dialog-in {
			& div.modal-content {
				transform: translate3d(0, 0, 0);
				animation: dialogIn 0.5s ease-in-out;
			}

			&::backdrop {
				opacity: 1;
				animation: fadeIn 0.5s ease-in-out;
			}
		}

		&.dialog-out {
			& > div {
				transform: translate3d(0, 100vh, 0);
				animation: dialogOut 0.5s ease-in-out;
			}

			&::backdrop {
				opacity: 0;
				animation: fadeOut 0.5s ease-in-out;
			}
		}

		&::backdrop {
			background-color: theme('colors.black/48%');
		}
	}
</style>
